import { IUser } from '../user';
import { CommonAttributes } from './newFlowCampaign';
import { ICompanyLocation, IServiceArea } from '../common';

export interface ICampaignGoal {
	id: string;
	target?: number | string;
}

export enum ECampaignPromotedType {
	catalogs = 'catalogs',
	landingPage = 'landingPage',
}

export interface ICampaignPromotedObject {
	type: ECampaignPromotedType;
	ids: string[] | [];
}

export interface ICampaignPromotion {
	code: string;
	description: string;
}

export interface ICampaignBudgetBreakdown {
	id: string;
	type: string;
	budget: number;
}

export interface ICampaignBudget {
	currency: string;
	total: number;
	breakdown?: Array<ICampaignBudgetBreakdown>;
}

export interface ICampaign {
	id: string;
	group: string;
	status: string;
	title: string;
	description: string;
	destination: string;
	audience: Array<any>;
	tone: Array<any>;
	promotedObject: ICampaignPromotedObject;
	promotion: Array<ICampaignPromotion>;
	channels: Array<string>;
	budget: ICampaignBudget | null;
	brief?: ICampaignBrief;
	designDirections: Array<any>;
	creatives: Array<any>;
	schedule?: ICampaignSchedule;
	lastUpdatedBy: IUser;
	updatedAt: Date;
	placements?: Array<string>;
}

export interface ICampaignBriefGoal {
	id: string;
	title: string;
	description: string;
}

export interface ICampaignBrief {
	title?: string;
	description: string;
	goals?: Array<ICampaignBriefGoal>;
	prompt?: string;
}

export enum ECampaignScheduleType {
	'Evergreen' = 'Evergreen',
	'One-off' = 'One-off',
}

export interface ICampaignSchedule {
	type: ECampaignScheduleType;
	startDate: Date;
	endDate?: Date;
}

interface ImageDimension {
	width: string;
	height: string;
}

interface LayerPosition {
	top: number;
	left: number;
}

interface LayerDimension {
	width: number;
	height: number;
}

export interface ImageLayer {
	name: string;
	imageUrl: string;
	position: LayerPosition;
	dimensions: LayerDimension;
	type: string;
	primary: boolean;
	displayName: string;
	parentName: string;
	visible?: boolean;
}

export interface TextLayer {
	id: string;
	name: string;
	content: string;
	fontSize: number;
	orientation: string;
	color: string;
	position: LayerPosition;
	dimensions: LayerDimension;
	type: string;
	primary: boolean;
	displayName: string;
	parentName: string;
	visible?: boolean;
}

export interface IChannelMediaContent {
	dimensions: ImageDimension;
	layers: (ImageLayer | TextLayer)[];
	layeredFile: string;
	flatFile: string;
	changesApplied?: any[];
}
export interface IChannelMediaAttributes {
	primaryText?: string;
	headline?: string;
	cta?: string;
	image: IChannelMediaContent;
	templateId?: string;
}

export interface ICampaignInterpretations {
	url_industry?: {
		industryIds: string[];
		industryId?: string;
		name: string;
		parentId: string | null;
		categoryId: string;
		id?: string;
		entityType?: string;
	};
	url_source?: string;
	url_keywords?: string[];
	audiences?: string[];
}

export interface IChannelTextContent {
	id: string;
	text: string;
}

export interface IChannelEmailContent {
	id: string;
	subject: string;
	plainBody: string;
	htmlBody: string;
	CTA: string;
}

// Location type for campaign (can be populated object or ID string)
export interface ICampaignLocation extends ICompanyLocation {
	_id?: string;
	id?: string;
}

// Service area type for campaign (can be populated object or ID string)
export interface ICampaignServiceArea extends IServiceArea {
	_id?: string;
	id?: string;
}

export interface ICampaign {
	id: string;
	group: string;
	status: string;
	title: string;
	description: string;
	goal?: Array<ICampaignGoal>;
	destination: string;
	customerProfiles: Array<string>;
	promotedObject: ICampaignPromotedObject;
	promotion?: Array<ICampaignPromotion>;
	channels: Array<string>;
	budget: ICampaignBudget;
	brief?: ICampaignBrief;
	designDirections?: Array<IDesignDirection>;
	creatives?: Array<ICreative>;
	schedule?: Partial<ICampaignSchedule>;
	lastUpdatedBy?: IUser;
	updatedAt?: Date;
	placements?: string[];
	templateId?: string;
	interpretations?: ICampaignInterpretations;
	locations?: Array<ICampaignLocation>;
	serviceAreas?: Array<ICampaignServiceArea>;
}
export interface ICreative {
	id: string;
	channel: string;
	prompt: string;
	mediaType: string;
	name: string;
	status: string;
	variant: string;
	designDirectionId?: string;
	content: {
		output: IChannelTextContent | IChannelEmailContent;
	};
	attributes: CommonAttributes;
	locked?: boolean;
	placements?: string[];
	app?: string;
	adEngine?: 'creatomate' | 'photopea';
}

export interface ICampaignsListingItem {
	id: string;
	title: string;
	group: string;
	status: string;
	progress: number;
}

export interface ICampaigns {
	limit: number;
	page: number;
	results: Array<ICampaign>;
	totalPages: number;
	totalResults: number;
}

export interface ICreativeRegeneratePayload {
	creativeId: string;
	properties?: {
		prompt: string;
	};
}

export interface IDesignDirection {
	adEngine?: 'creatomate' | 'photopea';
	id: string;
	channel: string;
	mediaType: string;
	name: string;
	status: string;
	variant: string;
	content: {
		output: IChannelTextContent;
	};
	attributes: CommonAttributes;
	locked?: boolean;
	estimatedLeads?: number;
}

interface ICreatomateElement {
	name: string;
	source?: string;
	text?: string;
	x?: string;
	y?: string;
	width?: string;
	height?: string;
}

interface ICreatomateAttributes {
	elements: ICreatomateElement[];
	templateId?: string;
	width?: number;
	height?: number;
}

export const isCreatomateAttributes = (
	attributes: CommonAttributes,
): attributes is ICreatomateAttributes => {
	return (attributes as ICreatomateAttributes).elements !== undefined;
};
// export interface ICreative {
// 	id: string;
// 	channel: string;
// 	prompt: string;
// 	mediaType: string;
// 	name: string;
// 	status: string;
// 	variant: string;
// 	content: {
// 		output: IChannelTextContent | IChannelEmailContent;
// 	};
// 	attributes:
// 		| IChannelMediaAttributes
// 		| IChannelTextContent
// 		| IChannelEmailContent;
// 	locked?: boolean;
// 	designDirectionId?: string;
// }

// export interface IDesignDirection {
// 	adEngine?: 'creatomate' | 'photopea';
// 	id: string;
// 	channel: string;
// 	mediaType: string;
// 	name: string;
// 	status: string;
// 	variant: string;
// 	content: {
// 		output: IChannelTextContent;
// 	};
// 	attributes: IChannelMediaAttributes | IChannelTextContent;
// 	locked?: boolean;
// }
